import React from 'react';
import { useGamification } from '../../contexts/GamificationContext';
import {
  Trophy,
  Star,
  TrendingUp,
  Award,
  Users,
  BookOpen,
  Crown,
  Medal,
  Flame
} from 'lucide-react';
import BadgeIcon from '../BadgeIcon';

const GamificationDashboard = () => {
  const {
    userStats,
    badges,
    loading,
    getUnlockedBadges,
    getLevelProgress,
    getTopUsers,
    getUserRank,
    getRarityColor,
    getRarityName
  } = useGamification();

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-gray-200 dark:bg-gray-700 rounded-lg h-32 animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  const unlockedBadges = getUnlockedBadges();
  const topUsers = getTopUsers(5);
  const userRank = getUserRank();
  const levelProgress = getLevelProgress();

  const getLevelIcon = (level) => {
    if (level >= 10) return <Crown className="w-6 h-6 text-yellow-500" />;
    if (level >= 5) return <Medal className="w-6 h-6 text-purple-500" />;
    return <Star className="w-6 h-6 text-blue-500" />;
  };

  return (
    <div className="space-y-6">
      {/* Statistiques principales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Niveau et points */}
        <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              {getLevelIcon(userStats.level)}
              <span className="text-2xl font-bold">Niveau {userStats.level}</span>
            </div>
            <Trophy className="w-8 h-8 text-yellow-300" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Points</span>
              <span className="font-semibold">{userStats.totalPoints}</span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-2">
              <div
                className="bg-yellow-300 h-2 rounded-full transition-all duration-300"
                style={{ width: `${levelProgress}%` }}
              />
            </div>
            <div className="text-xs text-white/80">
              {userStats.experience} / {userStats.experienceToNext} XP
            </div>
          </div>
        </div>

        {/* Cours terminés */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <BookOpen className="w-8 h-8 text-green-500" />
            <span className="text-2xl font-bold text-gray-900 dark:text-white">
              {userStats.coursesCompleted}
            </span>
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Cours terminés
          </div>
        </div>

        {/* Série actuelle */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <Flame className="w-8 h-8 text-orange-500" />
            <span className="text-2xl font-bold text-gray-900 dark:text-white">
              {userStats.streak}
            </span>
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Jours consécutifs
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
            Meilleure: {userStats.longestStreak} jours
          </div>
        </div>

        {/* Classement */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <Users className="w-8 h-8 text-purple-500" />
            <span className="text-2xl font-bold text-gray-900 dark:text-white">
              #{userRank}
            </span>
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Rang global
          </div>
        </div>
      </div>

      {/* Badges récents */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 mb-6">
          <Award className="w-6 h-6 text-yellow-500" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Badges Obtenus ({unlockedBadges.length}/{badges.length})
          </h3>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {unlockedBadges.map((badge) => (
            <div
              key={badge.id}
              className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
            >
              <div className="mb-2"><BadgeIcon icon={badge.icon} /></div>
              <div className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                {badge.name}
              </div>
              <div className={`text-xs px-2 py-1 rounded-full ${getRarityColor(badge.rarity)}`}>
                {getRarityName(badge.rarity)}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                +{badge.points} pts
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Classement */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 mb-6">
          <Trophy className="w-6 h-6 text-yellow-500" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Classement
          </h3>
        </div>
        
        <div className="space-y-3">
          {topUsers.map((user, index) => (
            <div
              key={user.id}
              className={`flex items-center gap-4 p-3 rounded-lg ${
                user.isCurrentUser 
                  ? 'bg-primary/10 dark:bg-primary/20 border border-primary/20' 
                  : 'bg-gray-50 dark:bg-gray-700'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  user.rank === 1 ? 'bg-yellow-500 text-white' :
                  user.rank === 2 ? 'bg-gray-400 text-white' :
                  user.rank === 3 ? 'bg-orange-500 text-white' :
                  'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}>
                  {user.rank}
                </div>
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {user.name}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Niveau {user.level} • {user.coursesCompleted} cours
                  </div>
                </div>
              </div>
              
              <div className="ml-auto text-right">
                <div className="font-semibold text-gray-900 dark:text-white">
                  {user.points} pts
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {user.streak} jours
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Progression hebdomadaire */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 mb-6">
          <TrendingUp className="w-6 h-6 text-green-500" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Progression Cette Semaine
          </h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {userStats.thisWeekStudyTime}h
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Temps d'étude
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {userStats.certificatesEarned}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Certificats
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {userStats.badgesEarned}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Badges obtenus
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GamificationDashboard;
